import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

export type ClusterRow = {
  id: string;
  category: string;
  latitude: number;
  longitude: number;
  radius_m: number;
  report_count: number;
  first_reported_at: string;
  last_reported_at: string;
  severity: string | null;
  status: string;
  location_name: string | null;
};

export type HotspotAlert = {
  id: string;
  cluster_id: string;
  title: string;
  description: string | null;
  severity: string;
  status: string;
  location_name: string | null;
  latitude: number | null;
  longitude: number | null;
  radius_m: number | null;
  reported_at: string;
};

/** Fetch active hotspot alerts, newest first. */
export const getHotspotAlerts = createServerFn({ method: "GET" }).handler(async () => {
  const { createClient } = await import("@supabase/supabase-js");
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  const supabasePublic = createClient<Database>(process.env["SUPABASE_URL"]!, key, {
    auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });

  const { data, error } = await supabasePublic
    .from("hotspot_alerts")
    .select("id, cluster_id, title, description, severity, status, location_name, latitude, longitude, radius_m, reported_at")
    .eq("status", "active")
    .order("reported_at", { ascending: false })
    .limit(20);

  if (error) throw error;
  return (data ?? []) as HotspotAlert[];
});

/** Fetch clusters near a point, sorted by report count. */
export const listClustersNear = createServerFn({ method: "POST" })
  .inputValidator((data: { lat: number; lng: number; radiusKm?: number; limit?: number }) => data)
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
    const supabasePublic = createClient<Database>(process.env["SUPABASE_URL"]!, key, {
      auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
      global: {
        fetch: (input, init) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });

    const radius = data.radiusKm ?? 10;
    const dLat = radius / 111;
    const dLng = radius / (111 * Math.max(Math.cos((data.lat * Math.PI) / 180), 0.01));

    const { data: rows, error } = await supabasePublic
      .from("report_clusters")
      .select("id, category, latitude, longitude, radius_m, report_count, first_reported_at, last_reported_at, severity, status, location_name")
      .gte("latitude", data.lat - dLat)
      .lte("latitude", data.lat + dLat)
      .gte("longitude", data.lng - dLng)
      .lte("longitude", data.lng + dLng)
      .order("report_count", { ascending: false })
      .limit(data.limit ?? 20);

    if (error) throw error;
    return (rows ?? []) as ClusterRow[];
  });

/** Fetch other reports in the same cluster. */
export const listRelatedReports = createServerFn({ method: "POST" })
  .inputValidator((data: { clusterId: string; excludePostId?: string; limit?: number }) => data)
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
    const supabasePublic = createClient<Database>(process.env["SUPABASE_URL"]!, key, {
      auth: { persistSession: false, autoRefreshToken: false, storage: undefined },
      global: {
        fetch: (input, init) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });

    let q = supabasePublic
      .from("posts")
      .select("id, title, category, status, severity, location_name, latitude, longitude, created_at, upvotes, comment_count")
      .eq("cluster_id", data.clusterId)
      .order("created_at", { ascending: false })
      .limit(data.limit ?? 6);

    if (data.excludePostId) q = q.neq("id", data.excludePostId);

    const { data: rows, error } = await q;
    if (error) throw error;
    return rows ?? [];
  });

/** Recompute clusters and hotspot alerts (admin/service only). */
export const recomputeClusters = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
    const { data: isMod } = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "moderator" });
    if (!isAdmin && !isMod) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin.rpc("compute_report_clusters");
    await supabaseAdmin.rpc("generate_hotspot_alerts");
    return { ok: true };
  });
